import { observable, action, computed } from 'mobx'
import { has } from 'lodash'

import BaseListItem, {
  IBaseListItemUpdateOptions,
} from 'stores/base/BaseListItem'
import ModelStore from 'stores/model/ModelStore'
import Model from 'stores/model/Model'
import Field from 'stores/field/Field'
import ActionStore from 'stores/action/ActionStore'
import { UNCOMITTED_RECORD_ID_PREFIX } from 'stores/action/Action'
import ErrorStore from 'stores/error/ErrorStore'
import LayoutStore from 'stores/layout/LayoutStore'
import TabStore from 'stores/tab/TabStore'

import createError from 'utils/createError'
import getFindUniqueQuery from 'utils/getFindUniqueQuery'
import runQuery from 'utils/runQuery'
import isFieldValueInvalid from 'utils/isFieldValueInvalid'

export interface IRecordCreatePayload {
  id: string
  modelId: string
  value: { [fieldName: string]: any }
}

interface InvalidField {
  field: Field
  reason: string | undefined
}

class Record extends BaseListItem {
  @observable id: string
  @observable modelId: string
  @observable valueInDB: { [key: string]: any }

  constructor(record: IRecordCreatePayload) {
    super(record)

    this.id = record.id
    this.valueInDB = record.value || {}
    this.modelId = record.modelId
  }

  @computed
  get model(): Model {
    let model = ModelStore.get(this.modelId)
    if (!model) {
      throw createError('Record.model', 'Invalid modelId', {
        modelId: this.modelId,
      })
    }
    return model
  }

  /* This is the value of the record with all pending actions applied */
  @computed
  get value(): { [key: string]: any } {
    // If an action for this record exists, we need to take that into account when calculating the record value
    const relevantActions = ActionStore.actions.filter(
      a => a.recordId === this.id && a.type === 'update'
    ) // Create / Delete actions will not affect a record's value

    if (relevantActions.length == 0) {
      // If no actions were added on this record, then the valueInDB is the actual value too
      return this.valueInDB
    }

    // `relevantActions` will only have 1 action thanks to Action consolidation
    const action = relevantActions[0]

    // We'll now go over all fields in this record's model and "apply" changes to those fields from the action
    return this.model.fields.reduce((value, field) => {
      if (action.value[field.name] === undefined) {
        // First off, if this field was not changed, then there's nothing to do
        value[field.name] = this.valueInDB[field.name]
        return value
      }

      // If it did change, the new value is the value from the action
      value[field.name] = action.value[field.name]
      return value

      // Do not use nullish coalesce operator! We need to consider `null`s
    }, {} as any)
  }

  @computed
  get isCommitted(): boolean {
    return !this.id.startsWith(UNCOMITTED_RECORD_ID_PREFIX)
    // I'd like to use the number of actions on this record to get `isCommitted`, but since I need this information to calculate the number of actions itself, I have to resort to this barbarianism
  }

  @computed
  get dirtyFieldNames(): string[] {
    if (!this.isCommitted) {
      // If this is a non committed record, all fields are dirty
      return this.model.fields.map(f => f.name)
    }

    // Otherwise, all touched fields are dirty
    const relevantActions = ActionStore.actions.filter(
      a =>
        a.sessionId === TabStore.activeTab?.sessionId && a.recordId === this.id
    )

    const dirtyFieldNames = relevantActions.reduce((dirtyFieldNames, a) => {
      Object.keys(a.value).forEach(fieldName => {
        dirtyFieldNames.add(fieldName)
      })
      return dirtyFieldNames
    }, new Set() as Set<string>)

    return Array.from(dirtyFieldNames)
  }

  @computed
  get invalidFields(): InvalidField[] {
    return Object.keys(this.value)
      .map((fieldName): InvalidField | undefined => {
        const field = this.model.getFieldByName(fieldName)
        if (!field) {
          return undefined
        }

        const reason = isFieldValueInvalid(field, this.value[fieldName])
        if (!reason) {
          return undefined
        }

        return {
          field,
          reason,
        }
      })
      .filter((f): f is InvalidField => !!f)
  }

  @action
  update(
    record: Partial<IRecordCreatePayload>,
    options: IBaseListItemUpdateOptions = {}
  ) {
    has(record, 'value') &&
      (this.valueInDB = { ...this.valueInDB, ...record.value })

    super.update(record, options)
  }

  @action
  fetchRelations = async () => {
    try {
      if (!this.isCommitted) {
        return
      }

      await runQuery(getFindUniqueQuery(this.id))
    } catch (e) {
      ErrorStore.update({
        type: 'client',
        description: 'Unable to fetch record',
        dump: e.message,
      })
      LayoutStore.updateError({ visible: true })
    }
  }
}

export default Record
